import React, { useState, useEffect } from 'react';
import { Settings, Save, CheckCircle } from 'lucide-react';
import { notificationThresholdsAPI } from '../../services/api';

/**
 * Reusable threshold configuration card for module Setup tabs.
 *
 * Props:
 *   module      — string key matching the backend route (e.g. "morningActivity")
 *   yearId      — academic year id
 *   title       — card heading (default "Notification Thresholds")
 *   thresholds  — array of { key, label, description, defaultValue, min, max, suffix }
 */
export default function NotificationThresholdCard({ module, yearId, title = 'Notification Thresholds', thresholds }) {
  const [values, setValues]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  useEffect(() => {
    if (!module || !thresholds?.length) { setLoading(false); return; }
    setLoading(true);
    notificationThresholdsAPI.get(module, yearId)
      .then(res => {
        const data = res.data || {};
        const init = {};
        for (const t of thresholds) {
          init[t.key] = data[t.key] !== undefined ? data[t.key] : t.defaultValue;
        }
        setValues(init);
      })
      .catch(() => {
        const init = {};
        for (const t of thresholds) init[t.key] = t.defaultValue;
        setValues(init);
      })
      .finally(() => setLoading(false));
  }, [module, yearId]); // eslint-disable-line

  async function handleSave() {
    setSaving(true);
    try {
      await notificationThresholdsAPI.save(module, { thresholds: values, academic_year_id: yearId });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {}
    setSaving(false);
  }

  if (loading) return null;

  return (
    <div className="glass-card p-6 space-y-5">
      <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
        <Settings size={16} className="text-primary-500" />
        {title}
      </h3>

      {thresholds.map(t => (
        <div key={t.key}>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t.label}</label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={t.min ?? 0}
              max={t.max ?? 100}
              value={values[t.key] ?? t.defaultValue}
              onChange={e => setValues(v => ({ ...v, [t.key]: Number(e.target.value) }))}
              className="w-24 text-center text-lg font-bold border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none transition"
            />
            {t.suffix && <span className="text-sm text-slate-500">{t.suffix}</span>}
          </div>
          {t.description && <p className="text-xs text-slate-400 mt-1">{t.description}</p>}
        </div>
      ))}

      <div className="pt-2 border-t border-slate-100 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {saved
            ? <><CheckCircle size={13} /> Saved</>
            : saving
              ? 'Saving…'
              : <><Save size={13} /> Save Thresholds</>
          }
        </button>
      </div>
    </div>
  );
}
